import { DuplicateIcon } from '@heroicons/react/outline';
import { PhoneIcon } from '@heroicons/react/solid';
import { Box, IconButton, Typography, useTheme } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Notistack } from '@shared/components/Notistack/Notistack';
import { GridCell } from '@shared/components/grid/GridCell';
import { copyToClipboard, formatPhoneNumber, formatString, withQueryParams } from '@shared/commonFunctions';
import {
  ContactDetailsSection,
  ContactDetailsSectionWrapper,
  Contrast,
  ContrastLink,
  Flag,
  NameRow,
  SubSection,
  SubSections,
} from '@styles/ContactManager/HeaderStyles';
import { ContactDetailsWrapper, Row } from '@styles/StyledDiv';

import { Modal } from '@shared/components/modals/Modal';
import { CreditBureauScoreType, EBureauType, IContactHeaderData, ILeadData, TabIndexEnum } from '../interfaces';
import { ILead } from '../../Tabs/components/Leads/types/interfaces/interfaces';
import { requestGetDealIdByNumericId } from '../requests';
import { IAsset } from '../../Tabs/components/Assets/interfaces';
import { CalculatorModal } from '../../Tabs/components/Deals/components/Tiles/components/CalculatorModal';
import { IDealSummary } from '../../Tabs/components/Deals/interfaces';
import { IContactDetails } from '../../Tabs/components/Contact/components/OptOutModal/interfaces';
import { AssetOnReportsModal } from '../../Tabs/components/Deals/components/Tiles/components/AssetOnReportsModal';
import { AddReminderModal } from './AddReminderModal';
import { ContactChatModal } from './ContactChat/ContactChatModal';
import { CreditScore } from './CreditScore';
import { StateModal } from './StateDataModal';
import { StipRequestModal } from './StipRequest/StipRequestModal';
import { LeadNoAssetAssignedDataModal } from './LeadNoAssetAssignedModal';

interface IContactDetailsProps {
  onOpenCreditReport: (bureauType: EBureauType , pulledFicoTypes : CreditBureauScoreType[]) => void;
  assetId: string;
  leadId: string;
  leads: ILead[];
  dealId: string;
  contactId: string;
  leadData?: ILeadData;
  dealSummary: IDealSummary[] | [];
  assets: IAsset[];
  data?: IContactHeaderData;
}

const getLocalTime = (localTimeZone: string | null, currentLocalTime: string | null) => {
  if (!localTimeZone || !currentLocalTime) return '';
  try {
    const formattedTime = new Date(currentLocalTime).toLocaleString('en-US', {
      hour12: true,
      hour: 'numeric',
      minute: '2-digit'
    });
    return formattedTime + ' ' + localTimeZone;
  } catch (err) {
    console.error(err);
    return '';
  }
};

export const ContactDetails = ({
  data,
  contactId,
  assetId,
  leadId,
  leads,
  dealId,
  leadData,
  dealSummary,
  assets,
  onOpenCreditReport,
}: IContactDetailsProps) => {
  const theme = useTheme();

  const navigate = useNavigate();
  const [isStipsRequestModalOpen, setStipsRequestModalStatus] = useState(0);
  const [stateValue, setStateValue] = useState('');
  const [resetPosition, setResetPositon] = useState(0);
  const [openReminder, setReminderModal] = useState(0);
  const [calculatorModalPosition, setCalculatorModalPosition] = useState(0);
  const [assetOnReportsModal, setAssetOnReportsModal] = useState(0);
  const [currentTime, setCurrentTime] = useState('');
  const [IsAssetRequiredModel, setIsAssetRequiredModel] = useState(0);

  const contactDetails: IContactDetails = {
    firstName: data?.firstName ?? '',
    lastName: data?.lastName ?? '',
    email: data?.email ?? '',
    cellPhone: data?.cellPhone ?? '',
  };

  useEffect(() => {
    if (data?.localTimeZone && data?.currentLocalTime) {
      setCurrentTime(getLocalTime(data.localTimeZone, data.currentLocalTime));
      const intervalId = setInterval(() => {
        setCurrentTime(getLocalTime(data.localTimeZone, data.currentLocalTime));
      }, 60000);
      return () => clearInterval(intervalId);
    }
  }, [data?.localTimeZone, data?.currentLocalTime]);

  const selectedAsset = useMemo(() => assets?.find((asset) => asset.id === assetId), [assets, assetId]);

  const handleClick = useCallback(() => {
    setResetPositon((prevState) => prevState + 1);
  }, []);

  const handleClose = useCallback(() => {
    setResetPositon(0);
  }, []);

  const phoneNumber = useMemo(
    () => data?.cellPhone || data?.homePhone || data?.workPhone,
    [data?.cellPhone, data?.homePhone, data?.workPhone]
  );

  const assetName = useMemo(() => {
    const isEmptyAsset = !selectedAsset?.year && !selectedAsset?.make && !selectedAsset?.model;
    const assetName = isEmptyAsset
      ? 'Asset'
      : `${selectedAsset?.year ?? '-'} ${selectedAsset?.make ?? '-'} ${selectedAsset?.model ?? '-'}`;

    return assetName;
  }, [selectedAsset]);

  const onCopyEmail = useCallback(() => {
    navigator.clipboard.writeText(data?.email || '');
    Notistack.infoMessage('Copied');
  }, [data?.email]);

  const onCopyName = useCallback(() => {
    navigator.clipboard.writeText(`${data?.firstName} ${data?.lastName}`);
    Notistack.infoMessage('Copied');
  }, [data?.firstName, data?.lastName]);

  const onCopyAddress = useCallback((streetAndCity: string, otherAddress: string, avoidAlert?: boolean) => {
    navigator.clipboard.writeText(`${streetAndCity}\n${otherAddress}`);
    !avoidAlert && Notistack.infoMessage('Copied');
  }, []);

  const checkIsAssetPresent = useCallback(() => {
    const leadsWithAsset = leads.some((lead) => lead.assetId === assetId);
    if (leadsWithAsset && contactId && assetId) {
      setStipsRequestModalStatus((prevState) => prevState + 1);
    } else {
      setIsAssetRequiredModel((prevState) => prevState + 1);
    }
  }, [contactId, assetId, leads]);

  const formatPlace = useMemo(() => {
    const state = data?.state?.toUpperCase() ?? '';
    const unit = data?.unit ?? '';
    const city = data?.city ? `${data.city}, ` : '';
    const streetAndCity = `${data?.streetLine1 || data?.streetLine2 || ''} ${unit}`;
    const zip = ` ${data?.zip ?? ''}`;
    const isInvalidAddress = !(streetAndCity?.trim() || city?.trim() || state?.trim() || zip?.trim());

    return (
      !isInvalidAddress && (
        <Box onCopy={() => onCopyAddress(streetAndCity, `${city} ${state} ${zip}`, true)}>
          <GridCell
            line={2}
            text={streetAndCity}
            sx={{ maxWidth: '80%' }}
          />
          <Row>
            <GridCell
              line={1}
              text={`${city} ${state} ${zip}`}
              sx={{ maxWidth: '80%' }}
            />
            <DuplicateIcon
              width={20}
              color={theme.palette.info.light}
              onClick={() => onCopyAddress(streetAndCity, `${city} ${state} ${zip}`)}
            />
          </Row>
        </Box>
      )
    );
  }, [data?.city, data?.state, data?.streetLine1, data?.streetLine2, data?.unit, data?.zip]);

  const availableEmail = useMemo(() => data?.email ?? data?.workEmail ?? '', [data?.email, data?.workEmail]);

  const fullName = useMemo(
    () =>
      formatString(data?.firstName + ' ' + (data?.middleInitial || '') + ' ' + data?.lastName, false) +
      ' ' +
      (data?.suffix || ''),
    [data]
  );

  const navigateToDeal = async () => {
    if (leadData?.lead?.pickedNumericDealId) {
      const deal = await requestGetDealIdByNumericId(leadData?.lead?.pickedNumericDealId);
      if (deal && deal[0].dealId) {
        const route = withQueryParams(
          '/contact-manager',
          { id: contactId, dealId: deal[0].dealId, tab: TabIndexEnum.Deals },
          false
        );
        navigate(route);
      }
    }
  };

  return (
    <ContactDetailsWrapper>
      <ContactDetailsSectionWrapper
        flex="1"
        style={{ maxWidth: '30%', minWidth: '300px' }}
      >
        <ContactDetailsSection>
          <NameRow>
            <GridCell
              text={fullName}
              variant="h4"
              sx={{ maxWidth: '80%' }}
            />
            <DuplicateIcon
              width={20}
              color={theme.palette.info.light}
              onClick={onCopyName}
            />
          </NameRow>

          {formatPlace}

          <Row
            justify="space-between"
            id="phoneRow"
          >
            {!!phoneNumber && (
              <Box
                display="flex"
                alignItems="center"
              >
                <Contrast
                  bold
                  onClick={handleClick}
                >
                  {formatPhoneNumber(phoneNumber)}
                </Contrast>

                <IconButton onClick={() => copyToClipboard(phoneNumber)}>
                  <DuplicateIcon
                    width={18}
                    color={theme.palette.info.light}
                  />
                </IconButton>

                <IconButton>
                  <PhoneIcon
                    width={18}
                    color={theme.palette.info.light}
                  />
                </IconButton>
              </Box>
            )}

            <Contrast
              bold
              style={{ justifyContent: 'end' }}
              onClick={() => setReminderModal((prevState) => prevState + 1)}
            >
              Add Reminder
            </Contrast>

            <Modal
              hideBackdrop
              title="SMS History"
              dialogHeight="84vh"
              dialogWidth="27.75vw"
              open={!!resetPosition}
              resetPosition={resetPosition}
              onClose={handleClose}
            >
              {phoneNumber && (
                <ContactChatModal
                  contactNumber={phoneNumber}
                  contactId={contactId}
                  contactDetails={contactDetails}
                />
              )}
            </Modal>
          </Row>

          <Row
            justify="space-between"
            flexWrap="wrap"
          >
            <Contrast bold>
              {!!availableEmail && (
                <>
                  <ContrastLink
                    bold
                    href={`mailto:${availableEmail}`}
                  >
                    {formatString(availableEmail)}
                  </ContrastLink>

                  <DuplicateIcon
                    width={20}
                    color={theme.palette.info.light}
                    onClick={onCopyEmail}
                  />
                </>
              )}
            </Contrast>
            <Typography>ID: {data?.numericId ?? ''}</Typography>
          </Row>

          <Row
            gap="22px"
            justify="left"
          >
            <span>
              State:
              <Contrast
                bold
                onClick={() => setStateValue(data?.state || '')}
              >
                {data?.state}
              </Contrast>
            </span>

            <span>Military: {data?.isMilitaryLendingActAvailable ? <Flag color="red">{'Yes'}</Flag> : 'No'}</span>

            <span>{currentTime}</span>
          </Row>
        </ContactDetailsSection>
      </ContactDetailsSectionWrapper>

      <ContactDetailsSectionWrapper
        flex="1"
        style={{ maxWidth: '40%', minWidth: '430px' }}
      >
        <ContactDetailsSection>
          <NameRow>
            <GridCell
              text={assetName}
              variant="h4"
              sx={{ maxWidth: '80%' }}
            />
          </NameRow>

          <SubSections>
            <SubSection width="40%">
              <Row>
                Deal:{' '}
                <Contrast
                  bold
                  onClick={navigateToDeal}
                >
                  {leadData?.lead?.pickedNumericDealId}
                </Contrast>
              </Row>

              <Row>
                Odometer: <Typography fontWeight="bold">{selectedAsset?.mileage}</Typography>
              </Row>

              <Row>
                VIN:{' '}
                <Typography
                  sx={{ textTransform: 'uppercase' }}
                  fontWeight="bold"
                >
                  {selectedAsset?.vin ?? ''}
                </Typography>
              </Row>

              <Row>
                <Contrast
                  bold
                  disabled={Boolean(!contactId)}
                  onClick={() => checkIsAssetPresent()}
                >
                  Request STIPS
                </Contrast>
              </Row>

              <Row>
                <Contrast
                  bold
                  onClick={() => setCalculatorModalPosition((prevState) => prevState + 1)}
                >
                  Calculators
                </Contrast>
              </Row>
            </SubSection>

            <SubSection width="55%">
              <Row>
                Lien Holder:
                <GridCell
                  sx={{ fontWeight: 'bold' }}
                  text={leadData?.asset?.lienHolderName}
                />
              </Row>

              <Row>
                Lien Amount: <b>${leadData?.asset?.lienAmount}</b>
              </Row>

              <Row>
                Lien Rate: <b>{leadData?.asset?.lienRate}%</b>
              </Row>

              <Row>
                Lien Payment: <b>${leadData?.asset?.lienMonthlyPayment}</b>
              </Row>
              <Row>
                <Contrast
                  bold
                  onClick={() => setAssetOnReportsModal((prevState) => prevState + 1)}
                >
                  Asset on Reports
                </Contrast>
              </Row>
            </SubSection>
          </SubSections>
        </ContactDetailsSection>
      </ContactDetailsSectionWrapper>

      {!!stateValue && (
        <StateModal
          stateValue={stateValue}
          handleClose={() => setStateValue('')}
        />
      )}

      {!!IsAssetRequiredModel && (
        <LeadNoAssetAssignedDataModal
          messageText="Lead does not have an asset assigned. Please open the leads tab and assign an asset before requesting stips"
          handleClose={() => setIsAssetRequiredModel(0)}
        />
      )}

      {!!isStipsRequestModalOpen && (
        <StipRequestModal
          stipModalPosition={isStipsRequestModalOpen}
          handleClose={() => setStipsRequestModalStatus(0)}
          leadId={leadId}
          leads={leads}
          assetsData={assets || []}
          contactId={contactId}
          assetId={assetId}
          assetName={`${assetName} ${leadData?.asset?.vin?.toUpperCase() ?? '-'}`}
          dealId={dealId}
          dealNumericId={leadData?.lead.pickedNumericDealId}
        />
      )}

      <ContactDetailsSectionWrapper
        flex="1"
        style={{ maxWidth: '30%', minWidth: '295px' }}
      >
        <CreditScore
          data={data?.creditScore}
          contactId={contactId}
          onOpenCreditReport={onOpenCreditReport}
        />
      </ContactDetailsSectionWrapper>
      {!!openReminder && (
        <AddReminderModal
          dealSummary={dealSummary}
          openReminder={openReminder}
          onClose={() => {
            setReminderModal(0);
          }}
        />
      )}
      {!!calculatorModalPosition && (
        <CalculatorModal
          calculatorModalPosition={calculatorModalPosition}
          onClose={() => setCalculatorModalPosition(0)}
        />
      )}
      {!!assetOnReportsModal && (
        <AssetOnReportsModal 
          assetId={assetId}
          onClose={() => setAssetOnReportsModal(0)}
        />
      )}
    </ContactDetailsWrapper>
  );
};
